import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Drawer, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { LoginContext } from '../../context/loginContext.js';
import {
  HomeOutlined,
  ContainerOutlined,
  LogoutOutlined,
  MenuOutlined,
  RightOutlined,
  LeftOutlined,
  HddOutlined,
  BookOutlined,
  BankOutlined,
  SwapOutlined,
  UsergroupAddOutlined,
  PictureOutlined,
  PrinterOutlined,
  ApartmentOutlined,
  TeamOutlined,
} from '@ant-design/icons';

const { Sider, Content } = Layout;

const MainLayout = ({ children, hideNavigation = false }) => {
  const { user, logout, selectedEmpresa, clearEmpresaSelection } = useContext(LoginContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (user?.tiu_descripcion !== 'Administrador') return;
    if (selectedEmpresa) return;
    if (location.pathname === '/select-empresa') return;
    navigate('/select-empresa');
  }, [user, selectedEmpresa, navigate, location.pathname]);

  const handleMenuClick = () => {
    if (isMobile) setMobileOpen(false);
  };

  const handleCambiarEmpresa = () => {
    clearEmpresaSelection();
    navigate('/select-empresa');
    if (isMobile) setMobileOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    if (isMobile) setMobileOpen(false);
  };

  const userNameNormalized = String(user?.usu_usuario || '').trim().toLowerCase();
  const canManageUsers = userNameNormalized === 'santiago2' || userNameNormalized === 'santiago';

  const menuItems = [
    { key: '1', icon: <HomeOutlined />, label: <Link to="/home-administrador">Inicio</Link> },
    {
      key: '2',
      icon: <SwapOutlined />,
      label: 'Movimientos',
      children: [
        { key: '2-1', label: <Link to="/nuevo-movimiento-administrador">Nuevo</Link> },
        { key: '2-2', label: <Link to="/lista-movimiento-administrador">Lista</Link> },
        { key: '2-3', label: <Link to="/importar-movimiento-administrador">Importar</Link> },
      ],
    },
    {
      key: '3',
      icon: <BankOutlined />,
      label: 'Pagos',
      children: [
        { key: '3-1', label: <Link to="/pago-administrador">Nuevo</Link> },
        { key: '3-2', label: <Link to="/lista-pago-administrador">Lista</Link> },
      ],
    },
    { key: '9', icon: <UsergroupAddOutlined />, label: <Link to="/cuenta-administrador">Cuentas</Link> },
    { key: '4', icon: <ContainerOutlined />, label: <Link to="/plan-cuenta-administrador">Plan de cuentas</Link> },
    { key: '8', icon: <PrinterOutlined />, label: <Link to="/reportes-administrador">Reportes</Link> },
    { key: '5', icon: <HddOutlined />, label: <Link to="/centro-costo-administrador">Centros de costos</Link> },
    { key: '6', icon: <BookOutlined />, label: <Link to="/tipos-comprobantes-administrador">Tipos Comprobantes</Link> },
    { key: '7', icon: <PictureOutlined />, label: <Link to="/localidad-administrador">Localidades</Link> },
    ...(canManageUsers
      ? [{ key: '10', icon: <TeamOutlined />, label: <Link to="/usuarios-administrador">Usuarios</Link> }]
      : []),
  ];

  const selectedKey = menuItems.find((item) => item.label?.props?.to === location.pathname)?.key;

  const footerLinkStyle = {
    padding: '12px 24px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 14,
    userSelect: 'none',
  };

  const SidebarFooter = () => (
    <div
      style={{
        flexShrink: 0,
        borderTop: '1px solid #303030',
        background: '#000000',
      }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={handleCambiarEmpresa}
        onKeyDown={(e) => e.key === 'Enter' && handleCambiarEmpresa()}
        style={{ ...footerLinkStyle, color: '#fff' }}
      >
        <ApartmentOutlined style={{ fontSize: 16 }} />
        {!collapsed || isMobile ? <span>Cambiar empresa</span> : null}
      </div>
      <div
        role="button"
        tabIndex={0}
        onClick={handleLogout}
        onKeyDown={(e) => e.key === 'Enter' && handleLogout()}
        style={{ ...footerLinkStyle, color: '#ff4d4f' }}
      >
        <LogoutOutlined style={{ fontSize: 16 }} />
        {!collapsed || isMobile ? <span>Cerrar sesión</span> : null}
      </div>
    </div>
  );

  const CollapseTrigger = () => (
    <div
      onClick={() => setCollapsed(!collapsed)}
      style={{
        flexShrink: 0,
        width: '100%',
        background: '#000000',
        textAlign: 'center',
        padding: '10px 0',
        cursor: 'pointer',
        color: '#666666',
        fontSize: '18px',
        borderTop: '1px solid #303030',
      }}
    >
      {collapsed ? <RightOutlined /> : <LeftOutlined />}
    </div>
  );

  const CompanyHeader = () => (
    <div
      style={{
        padding: '16px',
        textAlign: 'center',
        borderBottom: '1px solid #303030',
        marginBottom: '8px',
        flexShrink: 0,
      }}
    >
      <Typography.Text
        ellipsis={{ tooltip: selectedEmpresa?.emp_nombre || 'Seleccionar empresa' }}
        style={{
          color: '#fff',
          fontSize: collapsed && !isMobile ? '12px' : '16px',
          fontWeight: 'bold',
          display: 'block',
        }}
      >
        {hideNavigation
          ? 'Seleccionar empresa'
          : selectedEmpresa?.emp_nombre || 'Empresa'}
      </Typography.Text>
    </div>
  );

  const MenuScrollArea = () => (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <CompanyHeader />
      {hideNavigation ? (
        <div
          style={{
            padding: '16px 12px',
            color: '#888',
            textAlign: 'center',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          Elegí una empresa en el panel principal para habilitar el menú.
        </div>
      ) : (
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKey ? [selectedKey] : []}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ background: '#000000', color: '#666666', border: 'none' }}
        />
      )}
    </div>
  );

  const DesktopSidebar = () => (
    <Sider
      collapsible
      width={220}
      collapsedWidth={80}
      collapsed={collapsed}
      onCollapse={setCollapsed}
      theme="dark"
      trigger={null}
      style={{
        background: '#000000',
        color: '#666666',
        height: '100vh',
        position: 'sticky',
        top: 0,
        left: 0,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: '100vh',
        }}
      >
        <MenuScrollArea />
        <SidebarFooter />
        <CollapseTrigger />
      </div>
    </Sider>
  );

  if (!user) {
    return null;
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {isMobile && (
        <Button
          type="primary"
          icon={<MenuOutlined />}
          onClick={() => setMobileOpen(true)}
          style={{
            position: 'fixed',
            top: 15,
            left: 15,
            zIndex: 1000,
            background: '#666666',
            border: 'none',
            boxShadow: 'none',
          }}
        />
      )}

      {!isMobile && <DesktopSidebar />}

      <Drawer
        title={null}
        placement="left"
        onClose={() => setMobileOpen(false)}
        open={mobileOpen}
        width="100%"
        closable={false}
        bodyStyle={{
          padding: 0,
          background: '#000000',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
        maskStyle={{
          background: 'rgba(0, 0, 0, 0.8)',
        }}
      >
        <div
          style={{
            padding: '15px',
            background: '#000000',
            display: 'flex',
            justifyContent: 'flex-end',
            borderBottom: '1px solid #303030',
            flexShrink: 0,
          }}
        >
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setMobileOpen(false)}
            style={{
              color: '#666666',
              fontSize: '20px',
            }}
          />
        </div>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <CompanyHeader />
          {hideNavigation ? (
            <div
              style={{
                padding: '16px 12px',
                color: '#888',
                textAlign: 'center',
                fontSize: 13,
                flex: 1,
              }}
            >
              Elegí una empresa en el panel principal para habilitar el menú.
            </div>
          ) : (
            <Menu
              theme="dark"
              mode="inline"
              selectedKeys={selectedKey ? [selectedKey] : []}
              items={menuItems}
              onClick={handleMenuClick}
              style={{ background: '#000000', color: '#666666', border: 'none', flex: 1 }}
            />
          )}
          <SidebarFooter />
          <div
            onClick={() => setMobileOpen(false)}
            style={{
              flexShrink: 0,
              width: '100%',
              background: '#000000',
              textAlign: 'center',
              padding: '10px 0',
              cursor: 'pointer',
              color: '#666666',
              fontSize: '18px',
              borderTop: '1px solid #303030',
            }}
          >
            <LeftOutlined style={{ marginRight: 8 }} />
            Cerrar menú
          </div>
        </div>
      </Drawer>

      <Layout>
        <Content
          style={{
            padding: isMobile ? '60px 20px 20px 20px' : '20px',
            background: '#fff',
            minHeight: 360,
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
